import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState } from "react";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  RedditShareButton,
  EmailShareButton,
} from "react-share";
import LinkIcon from "@material-ui/icons/Link";
import FacebookSvgIcon from "@material-ui/icons/Facebook";
import LinkedInSvgIcon from "@material-ui/icons/LinkedIn";
import TwitterSvgIcon from "@material-ui/icons/Twitter";
import WhatsAppSvgIcon from "@material-ui/icons/WhatsApp";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
  },
  label: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "1.6px",
    marginRight: 8,
  },
  item: {
    width: 32,
    height: 32,
    minWidth: 32,
    border: "none",
    borderRadius: 8,
    background: "#4c004c !important",
    display: "inline-flex !important",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: "0 !important",
    color: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "& > svg, & .MuiSvgIcon-root": {
      width: "16px !important",
      height: "16px !important",
      fontSize: "16px !important",
      color: "#ffffff !important",
      fill: "#ffffff !important",
      display: "block",
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&:hover": {
      background: "#9b3b9b !important",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(155,59,155,0.45)",
    },
    "&:hover > svg, &:hover .MuiSvgIcon-root": {
      transform: "scale(1.1)",
    },
  },
  copyBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "transparent",
    border: "1px solid rgba(76,0,76,0.25)",
    color: "#4c004c",
    padding: 0,
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    marginLeft: 4,
    "&:hover": {
      background: "rgba(76,0,76,0.06)",
      borderColor: "#4c004c",
    },
    "& svg": {
      fontSize: 15,
    },
  },
});

const TelegramSvg = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.27 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const RedditSvg = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M22 12.08c0-1.06-.86-1.92-1.92-1.92-.52 0-.99.21-1.34.54-1.32-.96-3.13-1.57-5.13-1.65l.87-4.1 2.85.6c.03.73.62 1.31 1.36 1.31.75 0 1.36-.61 1.36-1.36s-.61-1.36-1.36-1.36c-.53 0-.99.31-1.21.76l-3.18-.67c-.06-.01-.13 0-.18.04-.05.04-.09.09-.1.16l-.97 4.55c-2.03.07-3.85.68-5.18 1.65-.35-.33-.82-.54-1.34-.54C2.86 10.16 2 11.02 2 12.08c0 .76.45 1.42 1.09 1.74-.03.18-.05.37-.05.55 0 2.83 3.32 5.13 7.41 5.13s7.41-2.3 7.41-5.13c0-.18-.02-.37-.05-.55.65-.32 1.09-.97 1.09-1.74zM7 13.33c0-.69.56-1.25 1.25-1.25s1.25.56 1.25 1.25-.56 1.25-1.25 1.25S7 14.02 7 13.33zm6.55 3.31c-.81.81-2.36.87-2.81.87s-2-.06-2.81-.87c-.12-.12-.12-.32 0-.44.12-.12.32-.12.44 0 .51.51 1.6.69 2.37.69s1.86-.18 2.37-.69c.12-.12.32-.12.44 0 .12.12.12.32 0 .44zm-.18-2.06c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
  </svg>
);

export const SocialShare = ({ url, label = "Share", className }) => {
  const classes = useStyles();
  const [tooltipText, setTooltipText] = useState("Click to copy the URL");

  const targetUrl =
    url ||
    (typeof window !== "undefined" ? window.location.href : "");

  const handleCopy = () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(targetUrl)
      .then(() => {
        setTooltipText("Link copied to clipboard!");
        setTimeout(() => setTooltipText("Click to copy the URL"), 2000);
      })
      .catch(() => {});
  };

  return (
    <Box className={`${classes.root} ${className || ""}`}>
      {label && <Typography className={classes.label}>{label}</Typography>}
      <FacebookShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on Facebook"
      >
        <FacebookSvgIcon />
      </FacebookShareButton>
      <LinkedinShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on LinkedIn"
      >
        <LinkedInSvgIcon />
      </LinkedinShareButton>
      <TwitterShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on Twitter"
      >
        <TwitterSvgIcon />
      </TwitterShareButton>
      <WhatsappShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on WhatsApp"
      >
        <WhatsAppSvgIcon />
      </WhatsappShareButton>
      <TelegramShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on Telegram"
      >
        <TelegramSvg />
      </TelegramShareButton>
      <RedditShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share on Reddit"
      >
        <RedditSvg />
      </RedditShareButton>
      <EmailShareButton
        url={targetUrl}
        className={classes.item}
        aria-label="Share via Email"
      >
        <MailOutlineIcon />
      </EmailShareButton>
      <Tooltip title={tooltipText} arrow>
        <IconButton
          onClick={handleCopy}
          className={classes.copyBtn}
          aria-label="copy link"
        >
          <LinkIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
